/**
 * The warehouse this tablet is working in (owner, 2026-10-12).
 *
 * The top bar reads from widest to narrowest: instance, warehouse, gang. The
 * warehouse narrows everything under it — the gangs offered, the trucks
 * listed, which location a scanned name means.
 *
 * WHERE THE WAREHOUSES COME FROM. Not from the warehouse list: that also
 * holds catch-alls made by imports, which are nowhere a forklift works. A
 * warehouse is offered here only if a GANG belongs to it, and the gang list
 * carries each gang's warehouse, so one read answers both "which warehouses"
 * and "which gangs in each". From that:
 *
 * - no gang has a warehouse → the site has not set this up; there is no
 *   warehouse level at all, and the tablet behaves as it always did;
 * - one warehouse → the tablet takes it without asking, and shows it;
 * - several → the driver is asked once, straight after logging in.
 *
 * The choice is the TABLET's, not the operator's: a forklift stays in its
 * warehouse across shifts, so it is kept across log-ins (unlike the gang) and
 * changed from the top bar. The gang list is kept too, so a tablet that
 * starts out of range still knows its warehouses.
 *
 * Plain logic and plain storage: nothing here imports Vue or Nuxt.
 */

const CHOSEN_KEY = 'dockautomation.warehouse'
const GANGS_KEY = 'dockautomation.siteGangs'

export type Warehouse = { id: number, name: string }

/** A gang as the API lists it, as far as this site needs. */
export type GangEntry = { name: string, warehouseId: number | null, warehouseName: string }

type GangDto = { name?: string | null, warehouseId?: number | null, warehouseName?: string | null }

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function storage(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** The gang list as the API returned it, made safe: nameless rows dropped. */
export function mapGangEntries(rows: unknown): GangEntry[] {
  if (!Array.isArray(rows)) return []
  return (rows as GangDto[])
    .map(row => ({
      name: (row?.name ?? '').trim(),
      warehouseId: typeof row?.warehouseId === 'number' ? row.warehouseId : null,
      warehouseName: (row?.warehouseName ?? '').trim(),
    }))
    .filter(entry => entry.name !== '')
}

/** The warehouses a forklift can be working in: those a gang belongs to, by name. */
export function warehousesOf(gangs: GangEntry[]): Warehouse[] {
  const byId = new Map<number, Warehouse>()
  for (const gang of gangs) {
    if (gang.warehouseId === null || byId.has(gang.warehouseId)) continue
    byId.set(gang.warehouseId, { id: gang.warehouseId, name: gang.warehouseName || `Warehouse ${gang.warehouseId}` })
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
}

/** How many gangs work in a warehouse, for its button. */
export function gangsInWarehouse(gangs: GangEntry[], warehouseId: number): number {
  return gangs.filter(gang => gang.warehouseId === warehouseId).length
}

/**
 * The gangs to offer: this warehouse's, and any gang that was given no
 * warehouse (it works wherever it is sent). With no warehouse chosen — a site
 * without the level — every gang.
 */
export function gangNamesFor(gangs: GangEntry[], warehouseId: number | null): string[] {
  const offered = warehouseId === null
    ? gangs
    : gangs.filter(gang => gang.warehouseId === null || gang.warehouseId === warehouseId)
  return [...new Set(offered.map(gang => gang.name))]
}

/**
 * Whether a truck is this warehouse's. A truck docked to a gang with no
 * warehouse belongs to none in particular and is shown everywhere; so is
 * every truck on a tablet with no warehouse chosen.
 */
export function orderInWarehouse(orderWarehouseId: number | null, warehouseId: number | null): boolean {
  return warehouseId === null || orderWarehouseId === null || orderWarehouseId === warehouseId
}

export type Settled = {
  /** The warehouse in use, or null when there is none (or none chosen yet). */
  warehouse: Warehouse | null
  /** Several to pick from and nothing valid picked: the driver has to be asked. */
  mustChoose: boolean
}

/** What the tablet's remembered choice comes to, against the warehouses there are now. */
export function settleWarehouse(warehouses: Warehouse[], chosen: Warehouse | null): Settled {
  if (warehouses.length === 0) return { warehouse: null, mustChoose: false }
  if (warehouses.length === 1) return { warehouse: warehouses[0]!, mustChoose: false }
  // The name as it is now: a warehouse can be renamed after the tablet chose it.
  const still = chosen ? warehouses.find(warehouse => warehouse.id === chosen.id) : undefined
  return still ? { warehouse: still, mustChoose: false } : { warehouse: null, mustChoose: true }
}

// ── kept on the tablet ──────────────────────────────────────────────────────

export function loadChosenWarehouse(store: Store | null = storage()): Warehouse | null {
  try {
    const parsed = JSON.parse(store?.getItem(CHOSEN_KEY) ?? 'null') as Partial<Warehouse> | null
    return parsed && typeof parsed.id === 'number' && typeof parsed.name === 'string'
      ? { id: parsed.id, name: parsed.name }
      : null
  } catch {
    return null
  }
}

export function saveChosenWarehouse(warehouse: Warehouse | null, store: Store | null = storage()): void {
  try {
    if (warehouse) store?.setItem(CHOSEN_KEY, JSON.stringify(warehouse))
    else store?.removeItem(CHOSEN_KEY)
  } catch {
    // No storage: the choice still holds for this page.
  }
}

export function loadSiteGangs(store: Store | null = storage()): GangEntry[] {
  try {
    return mapGangEntries(JSON.parse(store?.getItem(GANGS_KEY) ?? '[]'))
  } catch {
    return []
  }
}

export function saveSiteGangs(gangs: GangEntry[], store: Store | null = storage()): void {
  try {
    store?.setItem(GANGS_KEY, JSON.stringify(gangs))
  } catch {
    // No storage: the list still holds for this page.
  }
}
